// QR Code utility functions for parsing and validation

/**
 * Parses a JSON-format QR code string into registration data
 * @param {string} qrString - JSON QR code string
 * @returns {Object} Parsed registration data
 */
export const parseQRCodeJSON = (qrString) => {
  let data;
  try {
    data = JSON.parse(qrString);
  } catch (e) {
    throw new Error('Invalid JSON QR code format');
  }

  if (!data.serialNumber || !data.registrationCode) {
    throw new Error('Missing required fields');
  }

  return {
    version: data.version || 1,
    serialNumber: data.serialNumber,
    registrationCode: data.registrationCode,
    codeExpiresAt: data.codeExpiresAt || null,
    environment: data.environment || 'prod',
    customerName: data.customerName || '',
    customerNumber: data.customerNumber || '',
    country: data.country || '',
    licenseTier: data.licenseTier || '',
    licenseExpiresAt: data.licenseExpiresAt || '',
    deviceId: data.deviceId || ''
  };
};

/**
 * Validates JSON QR code data
 * @param {Object} data - Parsed JSON QR data
 * @returns {Object} Validation result with isValid flag and errors array
 */
export const validateQRCodeJSON = (data) => {
  const errors = [];

  if (!data.serialNumber) {
    errors.push('Serial number is required');
  }

  if (!data.registrationCode) {
    errors.push('Registration code is required');
  }

  if (data.codeExpiresAt && new Date(data.codeExpiresAt) < new Date()) {
    errors.push('QR code has expired');
  }

  return { isValid: errors.length === 0, errors };
};

// Single-use activation label payload (4a): valid until a device registers with it, or
// until the label is revoked: generating a new label rotates the secret, and unregister and
// regenerate-key remove it server side (the portal then mints the replacement straight
// away). version:2 + type:"activation" lets the device parser distinguish it from
// the registration QR. Never contains a licenseKey. The app refuses a label whose environment
// is missing, blank or not exactly 'dev' or 'prod' (aiTriage SRS-110), so this throws rather
// than print one with a defaulted stage.
export const ACTIVATION_LABEL_ENVIRONMENTS = ['dev', 'prod'];

export const isActivationLabelEnvironment = (environment) =>
  ACTIVATION_LABEL_ENVIRONMENTS.includes(environment);

export const generateActivationQRJSON = (serialNumber, activationSecret, environment) => {
  if (!isActivationLabelEnvironment(environment)) {
    throw new Error('Activation label environment must be exactly dev or prod');
  }
  return JSON.stringify({ version: 2, type: 'activation', serialNumber, activationSecret, environment });
};

/**
 * Parses a QR code string, auto-detecting JSON or legacy CSV format
 * @param {string} qrString - The QR code data string
 * @returns {Object} Parsed data
 */
export const parseQRCodeData = (qrString) => {
  if (!qrString || typeof qrString !== 'string') {
    throw new Error('Invalid QR code data');
  }

  // Try JSON first
  if (qrString.trim().startsWith('{')) {
    return parseQRCodeJSON(qrString);
  }

  // Fall back to legacy CSV parsing
  return parseQRCodeCSV(qrString);
};

/**
 * Parses a legacy CSV QR code string
 * Format: customerName,customerNumber,serialNumber,deviceId,licenseKey,<unused>,country
 * Field 6 is a retired slot (formerly a per-licence device UDI). It is skipped rather
 * than reused so country stays at index 6 for labels printed before the removal.
 * @param {string} qrString - CSV QR code string
 * @returns {Object} Parsed license data
 */
export const parseQRCodeCSV = (qrString) => {
  const parts = qrString.split(',');

  if (parts.length < 5) {
    throw new Error('Invalid QR code format: insufficient data fields');
  }

  return {
    customerName: parts[0] || '',
    customerNumber: parts[1] || '',
    serialNumber: parts[2] || '',
    deviceId: parts[3] || '',
    licenseKey: parts[4] || '',
    country: parts[6] || ''
  };
};

/**
 * Validates parsed QR code data
 * @param {Object} data - Parsed QR code data
 * @returns {Object} Validation result with isValid flag and errors array
 */
export const validateQRCodeData = (data) => {
  const errors = [];

  // Required fields validation
  if (!data.serialNumber) {
    errors.push('Serial number is required');
  }

  if (!data.deviceId) {
    errors.push('Device ID is required');
  }

  if (!data.licenseKey) {
    errors.push('License key is required');
  }

  // Country code validation (ISO 3166-1 alpha-2)
  if (data.country && !isValidCountryCode(data.country)) {
    errors.push('Invalid country code format (must be 2-letter ISO code)');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Validates country code format (ISO 3166-1 alpha-2)
 * @param {string} countryCode - Country code to validate
 * @returns {boolean} True if valid
 */
export const isValidCountryCode = (countryCode) => {
  // Basic validation for 2-letter country code
  return /^[A-Z]{2}$/.test(countryCode);
};

/**
 * Formats license data for CSV export
 * @param {Object} license - License object
 * @returns {Object} Formatted data for CSV
 */
export const formatLicenseForCSV = (license) => {
  return {
    serialNumber: license.serialNumber || '',
    deviceId: license.deviceId || '',
    customerName: license.customerName || '',
    customerNumber: license.customerNumber || '',
    licenseTier: license.licenseTier || 'BASIC',
    country: license.country || '',
    durationMonths: license.durationMonths || 12,
    active: license.active ? 'Yes' : 'No',
    createdAt: license.createdAt || '',
    expiresAt: license.expiresAt || ''
  };
};

/**
 * Common country codes for validation/dropdown
 * This is a subset - expand as needed
 */
export const COUNTRY_CODES = [
  { code: 'US', name: 'United States' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'CA', name: 'Canada' },
  { code: 'AU', name: 'Australia' },
  { code: 'DE', name: 'Germany' },
  { code: 'FR', name: 'France' },
  { code: 'IT', name: 'Italy' },
  { code: 'ES', name: 'Spain' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'SE', name: 'Sweden' },
  { code: 'NO', name: 'Norway' },
  { code: 'DK', name: 'Denmark' },
  { code: 'FI', name: 'Finland' },
  { code: 'JP', name: 'Japan' },
  { code: 'CN', name: 'China' },
  { code: 'IN', name: 'India' },
  { code: 'BR', name: 'Brazil' },
  { code: 'MX', name: 'Mexico' },
  { code: 'ZA', name: 'South Africa' },
  { code: 'SG', name: 'Singapore' }
];